import { useEffect, useMemo } from 'react';
import { useQuery } from 'react-query';
import { getAccountName, getBankList } from '../../pages/Settings/settingsAPI';

type Bank = {
  name: string;
  code: string;
}

type UseNGNBankAccountResolutionProps = {
  countryCode: string;
  bankName: string;
  accountNumber: string;
  onAccountNameResolved: (accountName: string) => void;
  onError?: (error: Error) => void;
  onLoading?: (isLoading: boolean) => void;
  isEnabled?: boolean;
}

export const useNGNBankAccountResolution = ({
  countryCode,
  bankName,
  accountNumber,
  onAccountNameResolved,
  onError,
  onLoading,
  isEnabled = true
}: UseNGNBankAccountResolutionProps) => {
  const { data: listOfBanks } = useQuery(
    ['bankList', countryCode], 
    () => getBankList(countryCode),
    {
      enabled: isEnabled && !!countryCode
    }
  );

  const { bankLookupMap, mappedBankList } = useMemo(() => {
    if (!isEnabled || !listOfBanks) {
      return { bankLookupMap: {}, mappedBankList: [] };
    }

    const bankMap: Record<string, string> = {};
    const bankArray = listOfBanks?.map((bank: Bank) => {
      bankMap[bank.name] = bank.code;
      return { label: bank.name, value: bank.name };
    }) || [];
    
    return { bankLookupMap: bankMap, mappedBankList: bankArray };
  }, [listOfBanks]);

  const selectedBankCode = bankName ? bankLookupMap[bankName] : '';

  useEffect(() => {
    if (!isEnabled || accountNumber?.length !== 10 || !selectedBankCode) return;

    const resolveBankAccountName = async () => {
      try {
        onLoading?.(true)
        const resolvedAccountName = await getAccountName(accountNumber, selectedBankCode);
        onAccountNameResolved(resolvedAccountName?.data?.account_name);
        onLoading?.(false)
      } catch (error) {
        onError?.(error as Error);
        onLoading?.(false)
      }
    };

    resolveBankAccountName();
  }, [accountNumber, selectedBankCode]);

  return {
    mappedBankList,
    bankLookupMap,
    selectedBankCode,
  };
};